import { createDefaultLogger } from './logging';
import { OneSightTrackingService } from './tracking-service';
import { Implementer } from './datalayer';
import { NoOperationTrackingService } from './noop-tracking-service';
export const isPreviewMode = () => {
    return new URLSearchParams(window?.location?.search)?.get('preview') === 'true';
};
export const isSSR = () => {
    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    return typeof window !== 'undefined' && !!window.__SSR__;
};
/**
 * Method to create and fully bootstrap a oneAudiOS compatible TrackingService.
 * After calling this, you can directly start with tracking.
 *
 * Does not track anything in Server-side Rendering (SSR) or Contentful preview mode.
 *
 * @param options Configuration
 *
 *
 * Example:
 * ```
 * const trackingService = createAndBootstrapTrackingService({
 *     appId: 'oneaudi-myapp-id',
 *     appName: __FEATURE_APP_NAME__,
 *     appVersion: __FEATURE_APP_VERSION__
 * });
 * trackingService.track({ ... });
 * ```
 */
export function createAndBootstrapTrackingService(options) {
    const trackingService = createTrackingService(options.appId, options.init?.datalayer, options.init?.urlSanitizer, options.init?.logger);
    trackingService.featureAppName = options.appName;
    trackingService.updateComponent({
        componentInfo: {
            componentID: options.appId,
        },
        attributes: {
            implementer: options.implementer ?? Implementer.UNKNOWN,
            version: options.appVersion,
            absoluteNumber: options.absoluteNumber ?? 1,
            instanceNumber: options.instanceNumber ?? 1,
        },
    });
    return trackingService;
}
/**
 * Method to create a oneAudiOS compatible TrackingService.
 * Make sure to set `featureAppName` afterwards.
 *
 * Does not track anything in Server-side Rendering (SSR) or Contentful preview mode.
 *
 * Certain methods will throw an error if the feature app's name is not defined. Setting
 * `trackingService.featureAppName` ensures the existence of the component.
 *
 * @param appId Mandatory: id of the feature app which wants to use tracking
 * @param datalayer Optional: The datalayer instance. If absent, a new instance will be created. Existing
 * `window.digitalData` will be reused
 * @param urlSanitizer Optional: The sanitizer to be used for URLs. If absent, a default will be used which strips away personal information.
 * @param logger Optional: The Logger. If absent, the console will be used for logging
 *
 * Example:
 * ```
 * const trackingService = createTrackingService('app-id');
 * trackingService.featureAppName = 'my-feature-app';
 * trackingService.track({ ... });
 * ```
 */
export function createTrackingService(appId, datalayer, urlSanitizer, logger) {
    const previewMode = isPreviewMode();
    if (previewMode || isSSR()) {
        (logger ?? createDefaultLogger()).info(`App '${appId}' is running in the ${previewMode ? 'Preview Mode' : 'Server Side Rendering mode'}. Client-side Tracking is disabled.`);
        return new NoOperationTrackingService(appId);
    }
    return new OneSightTrackingService(appId, datalayer, urlSanitizer, logger);
}
